// TREE OF KNOWLEDGE (PRD-920 / TOK-6) — Modules section in Org Settings.
// Fetches /api/v1/orgs/{org_id}/modules, renders the list with a per-module
// on/off toggle. Calls /api/v1/orgs/{org_id}/modules/{module_id} on toggle.
//
// Visibility is gated by `can_edit_organization` upstream in Settings.jsx —
// when that feature is off, this component isn't mounted.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { apiService } from '../../../services/api';
import './ModulesSection.css';

function ModulesSection({ orgId, onShowNotification }) {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Track per-module toggle in-flight so the switch reads as "saving" without
  // blocking other modules' toggles.
  const [busy, setBusy] = useState({});

  // App.js re-creates `showNotification` on every render (it isn't useCallback'd),
  // so this prop reference changes whenever a notification fires. Stash it in a
  // ref so `loadModules` only depends on `orgId` and won't be re-created on
  // every parent render — otherwise the load effect re-fires after each toggle's
  // success notification, briefly flashing the "Loading…" state over the list.
  const onShowNotificationRef = useRef(onShowNotification);
  useEffect(() => {
    onShowNotificationRef.current = onShowNotification;
  }, [onShowNotification]);

  const loadModules = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await apiService.getOrgModules(orgId);
      setModules(resp?.modules || []);
    } catch (err) {
      setError(err.message || 'Failed to load modules');
      onShowNotificationRef.current?.(err.message || 'Failed to load modules', 'error');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { loadModules(); }, [loadModules]);

  const handleToggle = async (module) => {
    const next = !module.enabled;
    setBusy((s) => ({ ...s, [module.module_id]: true }));
    // Optimistic — flip locally, roll back on error.
    setModules((rows) =>
      rows.map((m) => (m.module_id === module.module_id ? { ...m, enabled: next } : m))
    );
    try {
      const updated = await apiService.setOrgModule(orgId, module.module_id, next);
      setModules((rows) =>
        rows.map((m) => (m.module_id === module.module_id ? updated : m))
      );
      onShowNotification?.(
        `${module.name} ${next ? 'enabled' : 'disabled'} for this organization.`,
      );
    } catch (err) {
      // Roll back.
      setModules((rows) =>
        rows.map((m) => (m.module_id === module.module_id ? { ...m, enabled: !next } : m))
      );
      onShowNotification?.(err.message || 'Toggle failed', 'error');
    } finally {
      setBusy((s) => {
        const copy = { ...s };
        delete copy[module.module_id];
        return copy;
      });
    }
  };

  return (
    <div className="settings-org-card modules-section">
      <h3 className="settings-org-card-title">Modules</h3>

      {loading && <p className="modules-section-status">Loading…</p>}
      {error && !loading && <p className="modules-section-error">{error}</p>}
      {!loading && !error && modules.length === 0 && (
        <p className="modules-section-status">No modules available.</p>
      )}

      {!loading && !error && modules.length > 0 && (
        <ul className="modules-section-list">
          {modules.map((m) => (
            <li key={m.module_id} className="modules-section-row">
              <div className="modules-section-row-meta">
                <div className="modules-section-row-name">{m.name}</div>
                {m.description && (
                  <div className="modules-section-row-description">{m.description}</div>
                )}
              </div>
              <label className="resui-switch">
                <input
                  type="checkbox"
                  className="resui-switch-input"
                  checked={!!m.enabled}
                  disabled={!!busy[m.module_id]}
                  onChange={() => handleToggle(m)}
                />
                <span className="resui-switch-track"><span className="resui-switch-thumb" /></span>
                <span className="resui-switch-label">{m.enabled ? 'Enabled' : 'Disabled'}</span>
              </label>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default ModulesSection;
